import { prefersReducedMotion } from '@theme/utilities';

/**
 * Custom element for parallax background effect.
 * Moves the background at a slower rate than scroll to create depth.
 */
class ParallaxBackground extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {HTMLElement | null} */
  #imageElement = null;

  /** @type {() => void} */
  #boundScrollHandler;

  connectedCallback() {
    // Skip parallax if user prefers reduced motion
    if (prefersReducedMotion()) {
      return;
    }

    this.#imageElement = this.querySelector('picture img, img:not(picture img), svg, video');
    if (!this.#imageElement) return;

    // Bind scroll handler
    this.#boundScrollHandler = this.#onScroll.bind(this);

    // Set up IntersectionObserver to only animate when visible
    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        this.#isVisible = entry.isIntersecting;
        if (this.#isVisible) {
          window.addEventListener('scroll', this.#boundScrollHandler, { passive: true });
          this.#updatePosition();
        } else {
          window.removeEventListener('scroll', this.#boundScrollHandler);
        }
      },
      { threshold: 0, rootMargin: '100px' }
    );

    this.#observer.observe(this);

    // Initial position update
    this.#updatePosition();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#boundScrollHandler);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Get the parallax speed from data attribute.
   * Lower values = more parallax effect.
   * @returns {number}
   */
  get speed() {
    return parseFloat(this.dataset.speed || '') || 0.5;
  }

  #onScroll() {
    requestAnimationFrame(() => this.#updatePosition());
  }

  #updatePosition() {
    if (!this.#imageElement) return;

    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate scroll progress relative to viewport
    // When section top is at viewport bottom: progress = 0
    // When section bottom is at viewport top: progress = 1
    const scrollProgress = (viewportHeight - rect.top) / (viewportHeight + rect.height);

    // Clamp to 0-1 range
    const progress = Math.max(0, Math.min(1, scrollProgress));

    // Calculate parallax offset
    // At progress 0.5 (centered), offset = 0
    // Positive offset moves image down, negative moves up
    // We want background to move SLOWER (less distance) than content
    const parallaxRange = rect.height * 0.2; // 20% of height for parallax travel
    const offset = (progress - 0.5) * parallaxRange * (1 - this.speed);

    this.#imageElement.style.transform = `translate3d(0, ${offset}px, 0)`;
  }
}

if (!customElements.get('parallax-background')) {
  customElements.define('parallax-background', ParallaxBackground);
}

